import '@gl/lit/components/base/card/index.js';
import '../../base/tab_group/index.js';
import {html, render} from 'lit';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import styles from './style.scss';
import type {
  DestinationFilter,
  Hash,
  Policies,
} from '@gl/lit/types/audiences.js';
import {
  AudiencePerformanceInsights,
  AudienceShowTabs,
} from '@gl/lit/types/audiences.js';
import type {
  MatchRate,
  MatchRatesResponse,
} from '@gl/lit/types/audience_exports.js';
import {TabVariant} from '@gl/lit/types/tabs.js';
import {consume} from '@lit-labs/context';
import {audiencePolicyContext} from '@gl/lit/contexts/audience_policy/audience_policy_provider/context.js';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import {when} from 'lit/directives/when.js';
import type FlyTabGroup from '../../base/tab_group/index.js';
import FlyTab from '@gl/lit/components/base/tab/index.js';
import {SelectVariant} from '@gl/lit/components/base/select/index.js';
import {repeat} from 'lit/directives/repeat.js';
import {getQueryParameter} from '@gl/lit/utils/http.js';
import {Intent} from '@gl/lit/components/base/button/index.js';
import type {GetDestinationFiltersParams} from '@gl/lit/services/audiences.js';
import {audiencesService} from '@gl/lit/services/audiences.js';
import {audiencesLocale} from '@gl/lit/common/locale/audiences.js';
import {locale} from '@gl/lit/common/locale/index.js';
import {audienceExportsService} from '@gl/lit/services/audience_exports.js';
import '../../base/tooltip/index.js';
import type {SessionContextData} from '@gl/lit/contexts/session.js';
import {SessionContext} from '@gl/lit/contexts/session.js';
import type {AudienceExportController} from '@gl/lit/controllers/audience_export_controller/audience_export_controller.js';
import {
  AudienceExportControllerActions,
  audienceExportController,
} from '@gl/lit/controllers/audience_export_controller/audience_export_controller.js';
import {stateController} from '@gl/lit/controllers/state_controller/state_controller_decorators.js';
import {featureFlagsManager} from '@gl/lit/lib/feature_flags/FeatureFlagsManager.js';
import {audienceStore} from '@gl/stores/audience/index.js';
import {insightsLang} from '@gl/locale/translations/audiences/insights.js';

const lang = insightsLang[locale.current].shareResults;

/**
 * AudienceShowPanels -- Fly Tab Group implementation for v2 audience show page
 */

export const AUDIENCE_SHOW_PANELS_ELEMENT_TAG = 'audience-show-panels';
export const SWITCH_PANEL_EVENT_KEY = 'global:audiences:switch-panel';

export type SwitchPanelEventDetail = {
  panel: AudienceShowTabs;
};

enum QueryParams {
  ACTIVE_TAB = 'state',
  DESTINATION = 'destination',
  DASHBOARD = 'insight',
}

@customElement(AUDIENCE_SHOW_PANELS_ELEMENT_TAG)
@stateController(audienceExportController)
export default class AudienceShowPanels extends FlywheelComponent {
  controller!: AudienceExportController;

  @property({type: Boolean})
  hasExports = false;

  @property({type: Boolean})
  hasPerformance = false;

  @property({type: Boolean})
  hasQueries = false;

  @property({type: Boolean})
  hasInsights = false;

  @property({type: Array})
  audienceExportIDs: number[] = [];

  @property({type: Number})
  audienceId = -1;

  @consume({context: SessionContext, subscribe: true})
  @property({type: Object})
  session?: SessionContextData;

  destinationFilters: DestinationFilter[] = [];

  @consume({context: audiencePolicyContext, subscribe: true})
  @property({type: Object, attribute: false})
  policies?: Policies;

  @query('#audience-tab-group')
  $tabGroup?: FlyTabGroup;

  @queryAssignedElements({slot: 'exports'})
  $exportsNode?: HTMLElement[];

  get $matchRateRowCells(): HTMLElement[] {
    return (this.$exportsNode ?? [])
      .map(el =>
        Array.from(
          el.querySelectorAll<HTMLElement>('.audience_exports__match-rate')
        )
      )
      .flat();
  }

  @state()
  matchRates: MatchRatesResponse = {matchable: false, match_rates: []};

  static get locale() {
    return audiencesLocale[locale.current].show;
  }

  static get styles() {
    return [styles];
  }

  currentDestination() {
    return getQueryParameter(QueryParams.DESTINATION) || '';
  }

  currentDashboard() {
    return (
      getQueryParameter(QueryParams.DASHBOARD) ||
      AudiencePerformanceInsights[0].insight
    );
  }

  activeTab() {
    return getQueryParameter(QueryParams.ACTIVE_TAB);
  }

  isActiveTab(panelName: string): boolean {
    return this.activeTab() === panelName.toLowerCase();
  }

  private handleFilterDestination(evt: Event) {
    if (!(evt instanceof CustomEvent)) return;

    const selectedDestination = evt.detail.value;
    const params = new URLSearchParams(location.search);

    if (selectedDestination !== this.currentDestination()) {
      if (selectedDestination) {
        params.set(QueryParams.DESTINATION, selectedDestination);
      } else {
        params.delete(QueryParams.DESTINATION);
      }

      // Eventually, we'll use Looker Embed SDK to avoid doing a full page refresh.
      // But this is acceptable for now until we get that set up.
      location.href = `?${params.toString()}`;
    }
  }

  private handleChooseDashboard(evt: Event) {
    if (!(evt instanceof CustomEvent)) return;

    const selectedDashboard = evt.detail.value;
    const params = new URLSearchParams(location.search);

    if (selectedDashboard && selectedDashboard !== this.currentDashboard()) {
      params.set(QueryParams.DASHBOARD, selectedDashboard);

      // Eventually, we'll use Looker Embed SDK to avoid doing a full page refresh.
      // But this is acceptable for now until we get that set up.
      location.href = `?${params.toString()}`;
    }
  }

  private async onExportsSlotChange(_evt: Event) {
    if (this.hasExports && !Object.values(this.matchRates.match_rates).length) {
      this.matchRates = await audienceExportsService.getMatchRates(
        this.audienceExportIDs
      );
    }

    this.$matchRateRowCells.forEach(cell => {
      if (this.matchRates.matchable) {
        cell.classList.remove('hidden');
      }
      if (cell.textContent?.trim()) return;

      const id: number = parseInt(cell.getAttribute('data-aeid') ?? '', 10);
      const matchRate: MatchRate = this.matchRates.match_rates[id] ?? {
        value: 'unavailable',
        text: '',
      };

      render(
        html`<fly-tooltip content=${matchRate.text} ?disabled=${!matchRate.text}
          >${matchRate.value}</fly-tooltip
        >`,
        cell
      );
    });
  }

  updatePermalink(evt: Event) {
    if (!(evt.target instanceof FlyTab)) {
      throw Error('Attempted to update permalink with an invalid target.');
    }

    try {
      const url = `?${QueryParams.ACTIVE_TAB}=${(<FlyTab>(
        evt.target
      )).panel.toLowerCase()}`;
      history.replaceState({}, '', url);
    } catch (err) {
      console.error(err);
    }
  }

  renderTab(tabName: AudienceShowTabs) {
    return html`
      <fly-tab
        id="tab-${tabName}"
        slot="nav"
        panel="${tabName}"
        ?active=${this.isActiveTab(tabName)}
        @click=${this.updatePermalink}
      >
        ${(<Hash>AudienceShowPanels.locale.tabs)[tabName] ?? tabName}
      </fly-tab>
    `;
  }

  renderQueryCard() {
    return html`
      <div class="query-panel__container">
        <fly-card>
          <fly-tab-group>
            <fly-tab slot="nav" panel="sql"> SQL </fly-tab>
            <fly-tab slot="nav" panel="json"> JSON </fly-tab>
            <fly-tab-panel name="sql">
              <slot name="sql"></slot>
            </fly-tab-panel>
            <fly-tab-panel name="json">
              <slot name="json"></slot>
            </fly-tab-panel>
          </fly-tab-group>
        </fly-card>
      </div>
    `;
  }

  renderInsightsFilters() {
    const initialState = audienceStore.loopTracker.getState();
    const progress = initialState.getProgressData(this.audienceId);
    return html`
      <div class="filter-controls">
        <fly-select
          selectionRequired
          variant=${SelectVariant.CONDENSED_ROUNDED_SQUARE}
          initial=${this.currentDashboard()}
          .value=${this.currentDashboard()}
          @selected=${this.handleChooseDashboard}
        >
          ${repeat(
            AudiencePerformanceInsights,
            ({insight}) => insight,
            ({insight, icon}) =>
              html`<fly-list-item hasIcon value=${insight}>
                <fly-icon slot="icon" icon="${icon}"></fly-icon>
                ${(<Hash>AudienceShowPanels.locale.insights)[insight]}
              </fly-list-item>`
          )}
        </fly-select>

        <fly-select
          variant=${SelectVariant.CONDENSED_ROUNDED_SQUARE}
          placeholder=${AudienceShowPanels.locale.insights.filterByDestination}
          initial=${this.currentDestination()}
          .value=${this.currentDestination()}
          @selected=${this.handleFilterDestination}
        >
          <fly-list-item value="" ?disabled=${!this.destinationFilters.length}>
            ${AudienceShowPanels.locale.insights[
              this.destinationFilters.length ? 'showAll' : 'noDestinations'
            ]}
          </fly-list-item>
          ${repeat(
            this.destinationFilters,
            ({value}) => value,
            ({label, value, icon}) =>
              html`<fly-list-item hasIcon value=${value}>
                <img src="/assets/${icon}" alt="" height="18" slot="icon" />
                ${label}
              </fly-list-item>`
          )}
        </fly-select>
      </div>
      <div class="action-buttons">
        <fly-button
          href="/insights/channel_performance"
          size="sm"
          intent=${Intent.SECONDARY}
          hasIcon
          appendIcon
          class="view-more"
        >
          ${AudienceShowPanels.locale.insights.viewMoreInsights}
          <fly-icon slot="icon" icon="arrow_outward"></fly-icon>
        </fly-button>
        ${featureFlagsManager.enabled('loopTracker') &&
        progress?.status &&
        progress.status >= 66
          ? html`<fly-button
              size="sm"
              @click=${() => {
                this.controller.emitAction(
                  AudienceExportControllerActions.SHARE_RESULTS,
                  {
                    destinationCriteria: this.currentDestination(),
                    reportName: this.currentDashboard(),
                  }
                );
              }}
            >
              ${lang.modal.title}
            </fly-button>`
          : ''}
      </div>
    `;
  }

  async rendered() {
    const oldValue = this.destinationFilters;

    window.addEventListener(SWITCH_PANEL_EVENT_KEY, (e: Event) => {
      if (e instanceof CustomEvent && 'panel' in e.detail) {
        this.switchToPanel(e.detail.panel);
      }
    });

    if (this.hasInsights && oldValue.length === 0) {
      const params = {
        ...this.params,
        queryString: window.location.search,
      } as GetDestinationFiltersParams;
      this.destinationFilters =
        await audiencesService.getDestinationFilters(params);
      this.requestUpdate('destinationFilters', oldValue);
    }
  }

  /**
   * Switches to the specified panel without page reload
   * @param panelId - The ID of the panel to switch to
   */
  switchToPanel(panelId: AudienceShowTabs) {
    if (!this.$tabGroup) return;

    const targetTab = this.$tabGroup.querySelector<FlyTab>(`#tab-${panelId}`);
    if (!targetTab || targetTab.active) return;

    const url = `?${QueryParams.ACTIVE_TAB}=${panelId.toLowerCase()}`;
    history.replaceState({}, '', url);

    targetTab.click();
  }

  render() {
    return html`
      <fly-tab-group id="audience-tab-group" .variant=${TabVariant.PILL}>
        ${this.renderTab(AudienceShowTabs.AUDIENCES)}
        ${when(this.hasInsights, () =>
          this.renderTab(AudienceShowTabs.INSIGHTS)
        )}
        ${when(this.hasExports, () => this.renderTab(AudienceShowTabs.EXPORTS))}
        ${when(this.hasPerformance, () =>
          this.renderTab(AudienceShowTabs.PERFORMANCE)
        )}
        ${when(this.hasQueries, () => this.renderTab(AudienceShowTabs.QUERIES))}
        ${this.renderTab(AudienceShowTabs.SETTINGS)}

        <fly-tab-panel
          active
          id="panel-${AudienceShowTabs.AUDIENCES}"
          name="${AudienceShowTabs.AUDIENCES}"
        >
          <slot name="audience"></slot>
        </fly-tab-panel>

        <fly-tab-panel
          active
          id="panel-${AudienceShowTabs.INSIGHTS}"
          name="${AudienceShowTabs.INSIGHTS}"
        >
          <fly-card>
            <slot name="insights-filters">${this.renderInsightsFilters()}</slot>
            <slot name="insights"></slot>
          </fly-card>
        </fly-tab-panel>

        <fly-tab-panel
          active
          id="panel-${AudienceShowTabs.EXPORTS}"
          name="${AudienceShowTabs.EXPORTS}"
        >
          <slot name="exports" @slotchange=${this.onExportsSlotChange}></slot>
        </fly-tab-panel>
        <fly-tab-panel
          active
          id="panel-${AudienceShowTabs.PERFORMANCE}"
          name="${AudienceShowTabs.PERFORMANCE}"
        >
          <slot name="performance"></slot>
        </fly-tab-panel>
        <fly-tab-panel
          active
          id="panel-${AudienceShowTabs.QUERIES}"
          name="${AudienceShowTabs.QUERIES}"
        >
          <slot name="queries"></slot>
          ${this.renderQueryCard()}
        </fly-tab-panel>

        <fly-tab-panel
          active
          id="panel-${AudienceShowTabs.SETTINGS}"
          name="${AudienceShowTabs.SETTINGS}"
        >
          <slot name="settings"></slot>
        </fly-tab-panel>
      </fly-tab-group>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AUDIENCE_SHOW_PANELS_ELEMENT_TAG]: AudienceShowPanels;
  }
}
